import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';

import { ApiError } from 'src/app/interfaces/api';
import { Banner, ReferAFriend } from 'src/app/interfaces/strapi';

import StrapiService from 'src/app/services/strapi.service';
import { UserService } from 'src/app/services/user.service';
import { PageComponent } from 'src/app/shared/components/page/page.component';
import { Regexes } from 'src/app/shared/regexes';
import { NgIf, NgFor } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ClipboardModule } from 'ngx-clipboard';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { YoutubeVideoComponent } from '../../../shared/components/youtube-video/youtube-video.component';
import { PageRetrievalErrorComponent } from '../../../shared/components/page-retrieval-error/page-retrieval-error.component';
import { AuthService } from '../../../services/auth.service';
import { MarkedPipe } from '../../../shared/pipes/marked.pipe';

@Component({
    selector: 'app-refer-a-friend',
    templateUrl: './refer-a-friend.component.html',
    styleUrls: ['./refer-a-friend.component.scss'],
    imports: [
        BreadcrumbsComponent,
        NgIf,
        YoutubeVideoComponent,
        PageRetrievalErrorComponent,
        FormsModule,
        ReactiveFormsModule,
        NgFor,
        RouterLink,
        ClipboardModule,
        MarkedPipe,
    ],
})
export class ReferAFriendComponent extends PageComponent implements OnInit {
  public override pageData: ReferAFriend = <ReferAFriend>{};

  public referForm!: UntypedFormGroup;

  public submitted = false;

  public submissionComplete = false;

  public formErrorText = '';

  public submissionError = false;

  public linkCopied = false;

  public header: Banner | null = null;

  constructor(
    protected override readonly titleService: Title,
    protected override readonly metaService: Meta,

    public override readonly strapi: StrapiService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly userService: UserService,
    public readonly authService: AuthService
  ) {
    super(titleService, metaService, strapi);
  }

  ngOnInit(): void {
    this.referForm = this.formBuilder.group({
      emails: this.formBuilder.array([this.createEmailFormGroup()]),
    });

    this.strapi
      .getDocumentByUID('refer-a-friend', 'refer-a-friend', {
        populate: '*',
      })
      .subscribe({
        next: (data) => {
          if (data) {
            this.parsePageData(data.data.attributes as ReferAFriend);
            super.setMetas();
          }
        },
        error: () => {
          this.pageRetrievalError = true;
        },
      });

    this.strapi
      .getDocumentByUID('cta-banners', 'refer-a-friend-header', {
        'populate[image][populate]': '*',
      })
      .subscribe({
        next: (data) => {
          this.header = <Banner>data.data[0].attributes;
        },
      });
  }

  protected override parsePageData(page: ReferAFriend): void {
    const theData = page;

    super.parsePageData(page);

    this.pageData = theData;
  }

  public createEmailFormGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      emailAddress: new UntypedFormControl('', {
        validators: [
          Validators.email.bind(this),
          Validators.required.bind(this),
          Validators.pattern(Regexes.emailRegex),
        ],
        updateOn: 'blur',
      }),
    });
  }

  public addEmailFormGroup(): void {
    const emails = this.referForm.get('emails') as UntypedFormArray;
    if (emails.length < 11) emails.push(this.createEmailFormGroup());
    this.resetSubmitStatus();
  }

  public removeOrClearEmail(i: number): void {
    const emails = this.referForm.get('emails') as UntypedFormArray;
    if (emails.length > 1) {
      emails.removeAt(i);
    } else {
      emails.reset();
    }
    this.resetSubmitStatus();
  }

  public clearEmails(): void {
    const emails = this.referForm.get('emails') as UntypedFormArray;
    emails.clear();
    this.addEmailFormGroup();
  }

  public getControls(): AbstractControl[] {
    return (this.referForm.get('emails') as UntypedFormArray).controls;
  }

  public submitReferForm(): void {
    this.submitted = true;
    if (this.referForm.valid) {
      const formValue = <ReferFormValue>this.referForm.value;
      const emails = formValue.emails.map((email) => email.emailAddress);
      if (this.authService.currentUser?.uuid)
        this.userService
          .refer(this.authService.currentUser.uuid, emails)
          .subscribe({
            next: () => {
              this.submissionComplete = true;
            },
            error: (error: ApiError) => {
              this.submissionError = true;
              if (error.status === 404) {
                this.formErrorText = this.pageData.no_vouchers_message;
              }
            },
            complete: () => {
              this.submitted = false;
            },
          });
    }
  }

  private resetSubmitStatus(): void {
    this.submitted = false;
    this.submissionComplete = false;
    this.submissionError = false;
  }
}

export interface ReferFormValue {
  emails: {
    emailAddress: string;
  }[];
}
